import { rewordValidationMessages, ValidationError, type ErrorDetails } from '#api/errors.ts';
import { del, get, getList, patch, post } from '#api/fetch.ts';
import type { ResourceListOptions, SortDirection } from '#dn-types/api.ts';
import type { TagName } from '#dn-types/host.ts';
import type { Tag } from '#dn-types/tag.ts';

export const DEFAULT_TAGS_PAGE_SIZE = 500;

/**
 * Rewords the "too many allowed tags on a firewall rule" limit error, shared between tag creation and
 * editing. Mirrors the role endpoint's handling since tag firewall rules use the same shape.
 */
function rewordFirewallRuleTagsLimit(details: ErrorDetails): string | undefined {
  if (details.path && /firewallRules\[\d+\]\.allowedTags/.test(details.path) && details.code === 'ERR_LIMIT_EXCEEDED') {
    if (typeof details.extra?.limit === 'number') {
      return `You have reached the limit of ${details.extra.limit} tags for one or more of your firewall rules. Remove some of the tags before submitting again.`;
    }
    return 'You have the maximum number of tags allowed for one or more of your firewall rules. Remove some of the tags before submitting again.';
  }
}

// GET /v2/tags
export type GetTagsResult = Tag[];
type GetTagsVariables = ResourceListOptions & { sortDir?: SortDirection };
export function getTags({
  cursor,
  pageSize = DEFAULT_TAGS_PAGE_SIZE,
  // Sort descending so we get highest priority first
  sortDir = 'desc',
  init,
}: GetTagsVariables = {}) {
  const params = new URLSearchParams({
    includeCounts: 'true',
    pageSize: pageSize.toString(),
    sortDirection: sortDir,
  });

  if (cursor) {
    params.append('cursor', cursor);
  }

  return getList<GetTagsResult>(`/v2/tags?${params.toString()}`, init);
}

// GET /tags/{key:value}
export type GetTagResult = Tag;
export function getTag(tagName: TagName, init: RequestInit) {
  return get<GetTagResult>(`/v1/tags/${encodeURIComponent(tagName)}`, init);
}

/** Optionally position a tag relative to an existing one; at most one of before/after may be set */
type TagPosition = { before?: TagName; after?: never } | { after?: TagName; before?: never };

// POST /tags
// firewallRulesCount is a read-only derived field (like hostCount/priority) and is never submitted.
export type AddTagVariables = Omit<Tag, 'hostCount' | 'priority' | 'firewallRulesCount'> & TagPosition;
export type AddTagResult = Tag;

function isInvalidPositionalReference(err: unknown) {
  return (
    err instanceof ValidationError &&
    err.errors.some(
      (details) => details.code === 'ERR_INVALID_REFERENCE' && (details.path === 'before' || details.path === 'after')
    )
  );
}

export function addTag(formData: AddTagVariables) {
  return (
    post<Tag>('/v1/tags', formData)
      .catch((err: unknown) => {
        // The referenced tag can disappear between form load and submit (e.g. the source of a
        // duplicate was deleted in another tab). The new tag is still valid without a position,
        // so retry at the default position rather than failing the save.
        const { before, after, ...tagData } = formData;
        if ((before ?? after) !== undefined && isInvalidPositionalReference(err)) {
          return post<Tag>('/v1/tags', tagData);
        }
        throw err;
      })
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have a tag with this name.';
          }
          if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
            if (typeof details.extra?.limit === 'number') {
              return `You have reached the limit of ${details.extra.limit} tags. Delete some unused tags before creating more.`;
            }
            return 'You have the maximum number of tags allowed. Delete some unused tags before creating more.';
          }
          return rewordFirewallRuleTagsLimit(details);
        });
      })
  );
}

// PATCH /tags/name
/**
 * An omitted field leaves the stored value untouched; a present field replaces it wholesale (lists
 * included, there are no item-level operations). Omitting both before and after leaves the tag's
 * position unchanged.
 */
export type EditTagVariables = {
  name: TagName;
  variables: Partial<Pick<Tag, 'description' | 'configOverrides' | 'routeSubscriptions' | 'firewallRules'>> &
    TagPosition;
};
export type EditTagResult = Tag;
export function editTag(input: EditTagVariables) {
  return (
    patch<Tag>(`/v1/tags/${encodeURIComponent(input.name)}`, input.variables)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have a tag with this name.';
          }
          if ((details.path === 'before' || details.path === 'after') && details.code === 'ERR_INVALID_REFERENCE') {
            return 'Unable to reorder tag.  Please refresh the page and try again.';
          }
          if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
            if (typeof details.extra?.limit === 'number') {
              return `You have reached the limit of ${details.extra.limit} tags. Delete some unused tags before creating more.`;
            }
            return 'You have the maximum number of tags allowed. Delete some unused tags before creating more.';
          }
          return rewordFirewallRuleTagsLimit(details);
        });
      })
  );
}

// DELETE /tags/:name
export type DeleteTagResult = Record<string, never>;
export function deleteTag(name: string) {
  return (
    del<DeleteTagResult>(`/v1/tags/${encodeURIComponent(name)}`)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === undefined && details.code === 'ERR_HAS_DEPENDENTS') {
            return 'There are hosts that exist using this tag, please remove the tag first.';
          }
        });
      })
  );
}
